import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PolicyForm } from '../components/moderation/PolicyForm';
import { useAuth } from '../contexts/AuthContext';
import { CATEGORY_TEXT, apiError, gateRows, healthStatus, type PolicyFields } from '../lib/moderationView';
import {
  hubService,
  type ModerationEvalRun,
  type ModerationExampleRow,
  type ModerationHealth,
  type ModerationItem,
  type ModerationLexiconRow,
  type ModerationPolicyView,
  type ModerationQueueStats,
} from '../services/hub.service';

const TABS = ['health', 'queue', 'evaluation', 'lexicon', 'examples', 'default'] as const;
type Tab = (typeof TABS)[number];
const TAB_TEXT: Record<Tab, string> = {
  health: 'Health',
  queue: 'Relabel queue',
  evaluation: 'Evaluation',
  lexicon: 'Swear words',
  examples: 'Examples',
  default: 'Default policy',
};
const VERDICTS = ['abuse', 'spam', 'complaint', 'none'] as const;

/** Platform → Moderation: the data and the gate behind every bot's moderation (platform team only; the server enforces). */
export function ModerationAdmin() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') ?? '';
  const tab: Tab = (TABS as readonly string[]).includes(raw) ? (raw as Tab) : 'health';
  return (
    <div className="space-y-4 p-6">
      <h1 className="text-xl font-semibold">Comment moderation — platform</h1>
      <div role="tablist" className="inline-flex overflow-hidden rounded border border-gray-300 text-sm">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setParams(t === 'health' ? {} : { tab: t }, { replace: true })}
            className={`px-3 py-1 ${tab === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {TAB_TEXT[t]}
          </button>
        ))}
      </div>
      {tab === 'health' && <Health />}
      {tab === 'queue' && <Queue />}
      {tab === 'evaluation' && <Evaluation />}
      {tab === 'lexicon' && <Lexicon />}
      {tab === 'examples' && <Examples />}
      {tab === 'default' && <DefaultPolicy />}
    </div>
  );
}

/** One comment at a time; keys 1–4 label it, S skips. */
function Queue() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [stats, setStats] = useState<ModerationQueueStats | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [list, s] = await Promise.all([
      hubService.moderationQueue({ limit: 20 }, getToken()),
      hubService.moderationQueueStats(getToken()),
    ]);
    setItems(list.rows);
    setStats(s);
  }, [getToken]);

  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);

  const current = items[0];

  const label = useCallback(
    async (verdict: string) => {
      if (!current || busy) return;
      setBusy(true);
      setMsg(null);
      try {
        await hubService.moderationQueueVerdict(current.id, verdict, getToken());
        setStats(
          (s) =>
            s && { ...s, labelled: s.labelled + 1, byVerdict: { ...s.byVerdict, [verdict]: (s.byVerdict[verdict] ?? 0) + 1 } },
        );
        const rest = items.slice(1);
        setItems(rest);
        if (rest.length < 5) await load();
      } catch (e) {
        setMsg(apiError(e));
      } finally {
        setBusy(false);
      }
    },
    [current, busy, items, getToken, load],
  );

  const skip = useCallback(() => setItems((xs) => [...xs.slice(1), ...xs.slice(0, 1)]), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      const i = ['1', '2', '3', '4'].indexOf(e.key);
      if (i >= 0) label(VERDICTS[i]);
      else if (e.key.toLowerCase() === 's') skip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [label, skip]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Old comments from the legacy sentiment dataset. Decide what each one really is — these labels decide when bots may go
        live. Criticism of the business is a <b>complaint</b>, not abuse.
      </p>
      {stats && (
        <p className="text-sm">
          {stats.labelled} of {stats.total} labelled
          {Object.keys(stats.byVerdict).length > 0 &&
            ' — ' +
              Object.entries(stats.byVerdict)
                .map(([k, n]) => `${CATEGORY_TEXT[k] ?? k} ${n}`)
                .join(', ')}
        </p>
      )}
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      {current ? (
        <div className="rounded border border-gray-200 bg-white p-4">
          <p className="mb-4 text-lg">{current.text}</p>
          <div className="flex gap-2">
            {VERDICTS.map((v, i) => (
              <button
                key={v}
                type="button"
                disabled={busy}
                onClick={() => label(v)}
                className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="mr-1 text-gray-400">{i + 1}</span>
                {CATEGORY_TEXT[v]}
              </button>
            ))}
            <button type="button" onClick={skip} className="ml-auto text-sm text-gray-500">
              <span className="mr-1 text-gray-400">S</span>Skip
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Nothing left to label. If the queue was never filled, import the legacy dataset with the seed script.
        </p>
      )}
    </div>
  );
}

function Evaluation() {
  const { getToken } = useAuth();
  const [run, setRun] = useState<ModerationEvalRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    hubService
      .latestModerationEval(getToken())
      .then(setRun)
      .catch((e) => setMsg(apiError(e)));
  }, [getToken]);

  const runNow = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setRun(await hubService.runModerationEval(getToken()));
    } catch (e) {
      setMsg(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Runs today's detector over the labelled comments (newest 5,000). A category is ready for live when it is right ≥95% of the time on
        ≥200 labels; bots cannot switch to live until the categories they would hide or delete are ready.
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={runNow}
        className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
      >
        {busy ? 'Running…' : 'Run evaluation'}
      </button>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      {run ? (
        <>
          <p className="text-xs text-gray-500">
            Last run {new Date(run.at).toLocaleString()} by {run.ranBy ?? '—'} over {run.n} labels.
          </p>
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th>Category</th>
                <th>Precision</th>
                <th>Recall</th>
                <th>Labels</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {gateRows(run).map((r) => (
                <tr key={r.category} className="border-t border-gray-100">
                  <td className="py-1">{CATEGORY_TEXT[r.category]}</td>
                  <td>{r.precision}</td>
                  <td>{r.recall}</td>
                  <td>{r.support}</td>
                  <td className={r.ready ? 'text-green-700' : 'text-amber-700'}>{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {Object.keys(run.confusion).length > 0 && (
            <p className="text-xs text-gray-500">
              Mistakes (truth→detected):{' '}
              {Object.entries(run.confusion)
                .map(([k, n]) => `${k} ×${n}`)
                .join(', ')}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">No evaluation yet.</p>
      )}
    </div>
  );
}

function Lexicon() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<ModerationLexiconRow[]>([]);
  const [input, setInput] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => setRows(await hubService.moderationLexicon(getToken())), [getToken]);
  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);

  const add = async () => {
    try {
      const terms = input
        .split(/\n|,/)
        .map((t) => t.trim())
        .filter(Boolean);
      await hubService.addModerationLexicon(terms, getToken());
      setInput('');
      setMsg('Added. Every hub worker uses it within 5 minutes.');
      await load();
    } catch (e) {
      setMsg(apiError(e));
    }
  };

  const remove = (id: string) =>
    hubService
      .deactivateModerationLexicon(id, getToken())
      .then(load)
      .catch((e) => setMsg(apiError(e)));

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Whole words only (disguised spellings like Н*О*В*Ш are caught). Every word here hides comments on live bots — add only
        words that are always abusive.
      </p>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={3}
        placeholder="one per line"
        className="w-full rounded border border-gray-300 p-2 text-sm"
      />
      <button
        type="button"
        disabled={!input.trim()}
        onClick={add}
        className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
      >
        Add
      </button>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      <ul className="divide-y divide-gray-100 text-sm">
        {rows.map((r) => (
          <li key={r.id} className={`flex justify-between py-1 ${r.active ? '' : 'text-gray-400 line-through'}`}>
            <span>{r.term}</span>
            {r.active && (
              <button type="button" onClick={() => remove(r.id)} className="text-xs text-red-700">
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Examples() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<ModerationExampleRow[]>([]);
  const [category, setCategory] = useState<string>('abuse');
  const [input, setInput] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => setRows(await hubService.moderationExamples(getToken())), [getToken]);
  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
  }, [load]);

  const add = async () => {
    try {
      const items = input
        .split('\n')
        .map((t) => t.trim())
        .filter(Boolean)
        .map((text) => ({ category, text }));
      await hubService.addModerationExamples(items, getToken());
      setInput('');
      setMsg('Added. Every hub worker uses it within 10 minutes.');
      await load();
    } catch (e) {
      setMsg(apiError(e));
    }
  };

  const remove = (id: string) =>
    hubService
      .deactivateModerationExample(id, getToken())
      .then(load)
      .catch((e) => setMsg(apiError(e)));

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Example comments per category; new comments are compared with them by meaning. Add normal comments too, so ordinary
        questions stay normal.
      </p>
      <div className="flex gap-2">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        >
          {VERDICTS.map((v) => (
            <option key={v} value={v}>
              {CATEGORY_TEXT[v]}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!input.trim()}
          onClick={add}
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Add
        </button>
      </div>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={3}
        placeholder="one example per line"
        className="w-full rounded border border-gray-300 p-2 text-sm"
      />
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      {VERDICTS.map((v) => {
        const active = rows.filter((r) => r.category === v && r.active);
        return (
          <div key={v}>
            <p className="text-sm font-medium">
              {CATEGORY_TEXT[v]} ({active.length})
            </p>
            <ul className="ml-4 list-disc text-sm">
              {active.map((r) => (
                <li key={r.id}>
                  {r.text}{' '}
                  <button type="button" onClick={() => remove(r.id)} className="text-xs text-red-700">
                    remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/** The platform default row ('*','*'): bots without their own settings use it. Same live lock as bots. */
function DefaultPolicy() {
  const { getToken } = useAuth();
  const [view, setView] = useState<ModerationPolicyView | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    // Any bot's view carries `platform` (the default) and `run`; botId '0' never has its own row.
    hubService
      .getModerationPolicy('chatbot', '0', getToken())
      .then(setView)
      .catch((e) => setMsg(apiError(e)));
  }, [getToken]);

  const save = async (fields: PolicyFields) => {
    try {
      const v = await hubService.setPlatformModerationPolicy(fields, getToken());
      setView((old) => old && { ...old, platform: v.effective, run: v.run });
      setMsg('Platform default saved.');
    } catch (e) {
      setMsg(apiError(e));
      throw e;
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Used by every bot that has no settings of its own.</p>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      {view && <PolicyForm value={view.platform} run={view.run} onSave={save} />}
    </div>
  );
}

const SYSTEM_TEXT: Record<string, string> = { chatbot: 'v1 (chatbot_mn)', chatbot_v2: 'v2 (nsp_micro_channel)' };
const TONE_CLS = { green: 'border-green-200 bg-green-50', amber: 'border-amber-200 bg-amber-50', red: 'border-red-200 bg-red-50' };

/** B: are v1 / v2 sending comments, and do their actions land? (last 24 h, from hub's own records) */
function Health() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<ModerationHealth[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => {
    setMsg(null);
    setRows(await hubService.getModerationHealth(getToken()));
  }, [getToken]);
  useEffect(() => {
    load().catch((e) => setMsg(apiError(e)));
    const t = setInterval(() => load().catch(() => undefined), 60000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        What hub received from each system in the last 24 hours. A system that stopped sending shows here as quiet; a caller
        that cannot reach hub at all shows only in that service's logs (e.g. <code>moderation check unavailable</code>).
      </p>
      {msg && <p className="rounded bg-gray-100 p-2 text-sm">{msg}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((r) => {
          const st = healthStatus(r);
          return (
            <div key={r.system} className={`rounded border p-3 text-sm ${TONE_CLS[st.tone]}`}>
              <div className="flex justify-between">
                <span className="font-semibold">{SYSTEM_TEXT[r.system] ?? r.system}</span>
                <span>{st.text}</span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-gray-500">Checks, last hour</dt>
                <dd>{r.checks1h}</dd>
                <dt className="text-gray-500">Checks, 24 h</dt>
                <dd>{r.checks24h}</dd>
                <dt className="text-gray-500">Flagged, 24 h</dt>
                <dd>{r.flagged24h}</dd>
                <dt className="text-gray-500">Failed actions, 24 h</dt>
                <dd>{r.failed24h}</dd>
                <dt className="text-gray-500">Hides stuck pending</dt>
                <dd>{r.stuckPending}</dd>
                <dt className="text-gray-500">Avg check speed, last hour</dt>
                <dd>{r.avgLatencyMs1h == null ? '—' : `${r.avgLatencyMs1h} ms`}</dd>
                <dt className="text-gray-500">Last check</dt>
                <dd>{r.lastAt ? new Date(r.lastAt).toLocaleString() : '—'}</dd>
              </dl>
            </div>
          );
        })}
      </div>
    </div>
  );
}
